import { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import PageHeader from "@/components/PageHeader";
import { ErrorState } from "@/components/States";
import { TYPE_META } from "@/lib/format";
import { TrendingUp, TrendingDown, Search, Send, MessageCircle, Trophy } from "lucide-react";

const KPI_DEFS = [
  { key: "analyzed", label: "Perfiles analizados", icon: Search },
  { key: "contacted", label: "Contactos iniciados", icon: Send },
  { key: "conversations", label: "Conversaciones", icon: MessageCircle },
  { key: "clients", label: "Clientes", icon: Trophy },
];

const PERIODS = [
  { d: 7, label: "7 días", vs: "vs. 7 días anteriores", chart: "Actividad de los últimos 7 días" },
  { d: 30, label: "30 días", vs: "vs. 30 días anteriores", chart: "Actividad de los últimos 30 días" },
  { d: 90, label: "90 días", vs: "vs. 90 días anteriores", chart: "Actividad de las últimas 13 semanas" },
];

function Kpi({ def, data, vs }) {
  const delta = data?.delta;
  const hasDelta = typeof delta === "number";
  const positive = (delta || 0) >= 0;
  return (
    <div className="rounded-lg border border-brand-line bg-brand-panel p-5" data-testid={`kpi-${def.key}`}>
      <div className="flex items-center justify-between">
        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-brand-accent/[0.08] text-brand-accent">
          <def.icon size={17} />
        </span>
        {hasDelta && (
          <span className={`flex items-center gap-1 text-xs font-bold tnum ${positive ? "text-brand-pos" : "text-brand-neg"}`}>
            {positive ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
            {positive ? "+" : ""}{delta}
          </span>
        )}
      </div>
      <p className="mt-4 text-3xl font-extrabold tracking-tight text-brand-ink tnum">{data?.value ?? 0}</p>
      <p className="mt-1 text-sm text-brand-muted">{def.label}</p>
      <p className="mt-0.5 text-xs text-brand-muted">{hasDelta ? vs : "Sin periodo anterior para comparar"}</p>
    </div>
  );
}

export default function Metrics() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [days, setDays] = useState(7);
  const period = PERIODS.find((x) => x.d === days) || PERIODS[0];

  const load = useCallback(() => {
    setError(false);
    setData(null);
    api.get("/metrics", { params: { days } }).then((r) => setData(r.data)).catch(() => setError(true));
  }, [days]);

  useEffect(() => {
    load();
    window.addEventListener("ca:data-changed", load);
    return () => window.removeEventListener("ca:data-changed", load);
  }, [load]);

  // Escala con mínimo razonable: 1 o 2 acciones no deben llenar todo el gráfico (P-07).
  const maxChart = data ? Math.max(5, ...data.chart.map((c) => c.value)) : 5;
  const dense = data && data.chart.length > 14;

  return (
    <div className="space-y-8">
      <PageHeader
        title="Métricas"
        subtitle="Lo que está pasando con tu prospección, con datos reales de tu cuenta."
        testId="metrics-header"
      />

      <div className="ca-seg" role="tablist" aria-label="Periodo" data-testid="metrics-period">
        {PERIODS.map((p) => (
          <button key={p.d} type="button" role="tab" aria-selected={days === p.d} className={days === p.d ? "is-on" : ""}
            onClick={() => setDays(p.d)} data-testid={`metrics-period-${p.d}`}>{p.label}</button>
        ))}
      </div>

      {error && <ErrorState description="No hemos podido cargar tus métricas." onRetry={load} testId="metrics-error" />}

      {!error && data === null && (
        <div className="space-y-6" data-testid="loading-skeleton">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-32 rounded-lg" />)}
          </div>
          <div className="skeleton h-64 rounded-lg" />
        </div>
      )}

      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {KPI_DEFS.map((def) => <Kpi key={def.key} def={def} data={data.kpis[def.key]} vs={period.vs} />)}
          </div>

          <div className="grid gap-5 lg:grid-cols-3">
            {/* Chart */}
            <div className="rounded-lg border border-brand-line bg-brand-panel p-6 lg:col-span-2" data-testid="metrics-chart">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-brand-ink">{period.chart}</h3>
                <span className="text-xs text-brand-muted">Acciones realizadas</span>
              </div>
              <div className={`mt-8 flex h-48 items-end justify-between ${dense ? "gap-[3px]" : "gap-2 sm:gap-4"}`}>
                {data.chart.map((c, i) => (
                  <div key={i} className="flex min-w-0 flex-1 flex-col items-center gap-2" title={`${c.label}: ${c.value}`}>
                    {!dense && <span className="text-xs font-bold text-brand-ink tnum">{c.value}</span>}
                    <div className="flex w-full items-end justify-center" style={{ height: "150px" }}>
                      <div
                        className="w-full max-w-[36px] rounded-t-md bg-brand-accent transition-all duration-150"
                        style={{ height: `${Math.max(4, (c.value / maxChart) * 150)}px`, opacity: c.value === 0 ? 0.25 : 1 }}
                      />
                    </div>
                    <span className="text-xs font-semibold text-brand-muted" style={{ visibility: dense && i % 5 !== 0 && i !== data.chart.length - 1 ? "hidden" : "visible" }}>{c.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* By type */}
            <div className="rounded-lg border border-brand-line bg-brand-panel p-6" data-testid="metrics-by-type">
              <h3 className="font-bold text-brand-ink">Oportunidades por tipo</h3>
              <div className="mt-5 space-y-4">
                {Object.entries(TYPE_META).map(([k, v]) => {
                  const count = data.by_type[k] || 0;
                  const total = Object.values(data.by_type).reduce((a, b) => a + b, 0) || 1;
                  return (
                    <div key={k}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-brand-muted">{v.label}</span>
                        <span className="font-bold text-brand-ink tnum">{count}</span>
                      </div>
                      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-brand-ink/[0.06]">
                        <div className="h-full rounded-full bg-brand-accent" style={{ width: `${(count / total) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Learned */}
          <div className="rounded-lg border border-brand-line bg-brand-accent/[0.05] p-6">
            <h3 className="font-bold text-brand-ink">Lo que estás aprendiendo</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {(data.insights || []).map((l) => (
                <div key={l.key || l.t} className="rounded-md border border-brand-line bg-brand-panel p-4">
                  <p className="text-sm font-bold text-brand-ink">{l.t}</p>
                  <p className="mt-1 text-xs leading-relaxed text-brand-muted">{l.d}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
